import Modal from 'antd/lib/modal/Modal';
import { useAppDispatch, useAppSelector, useAppTranslation } from 'app/hooks';
import CloseIcon from 'assets/icons/CloseIcon';
import Typo from 'consts/typography';
import React, { useState } from 'react';
import { platformStates } from 'redux/platformSlice';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import { useHandleGetWallets } from 'hooks/useHandleGetWallets';
import PasteIcon from 'assets/icons/PasteIcon';
import SuccessIcon from 'assets/icons/SuccessIcon';
import userApi from 'api/userApi';
import { UsePromotionResponseModel } from 'models/user';
import useThemes from 'hooks/useThemes';
import { setShowConfetti } from 'features/home/homeSlice';

interface PromotionModalProps {
  title?: string;
  visible: boolean;
  closeModal: () => void;
  className?: string;
}

export const isValidAmount = (amount: string): boolean => {
  if (!amount.length) return true;
  if (amount.length === 2 && amount[0] === '0' && amount[1] !== '.') return false;
  if (amount.length === 10 && amount[9] === '.') return false;
  return true;
};

const PromotionModal: React.FunctionComponent<PromotionModalProps> = (props) => {
  const [promotionCode, setPromotionCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [usePromotionResponse, setUsePromotionResponse] =
    useState<UsePromotionResponseModel | null>(null);
  const { title = '', visible, closeModal, className = '' } = props;
  const { isPhone, isTablet } = useAppSelector(platformStates);
  const handleResponseError = useHandleResponseError();
  const { getWallet } = useHandleGetWallets();
  const t = useAppTranslation();
  const { currentTheme } = useThemes();
  const dispatch = useAppDispatch();

  const handlePasteFromClipboard = () => {
    navigator.clipboard.readText().then((data) => {
      setPromotionCode(data);
    });
  };

  const onSubmit = async () => {
    setIsLoading(true);
    const { ok, error, body } = await userApi.usePromotion({ promotionCode });

    setIsLoading(false);
    if (ok && body) {
      dispatch(setShowConfetti(true));
      getWallet();
      setUsePromotionResponse(body);
    }
    if (error) handleResponseError(error);
  };

  const onCloseModal = () => {
    resetModal();
    closeModal();
  };

  const resetModal = () => {
    setPromotionCode('');
    setUsePromotionResponse(null);
  };

  return (
    <Modal
      className={className}
      title={title}
      visible={visible}
      onCancel={onCloseModal}
      footer={null}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      width={600}
    >
      {!usePromotionResponse ? (
        <div className={`center-content-col gap-3 ${isPhone ? 'px-10' : 'px-50'}`}>
          <div className="w-full flex justify-center items-center gap-3">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color nowrap`}
              htmlFor="promotion-code"
            >
              {t('cashier.code')}
            </label>
            <div className="relative w-full">
              <input
                className="input-modal w-full"
                type="text"
                name="promotion-code"
                id="promotion-code"
                autoComplete="off"
                onChange={(event) => setPromotionCode(event.target.value)}
                value={promotionCode}
                placeholder={t('placeholder.enterPromotionCode')}
              />
              <PasteIcon
                isDarkmode={currentTheme === 'dark'}
                className="icon-input"
                onClick={handlePasteFromClipboard}
              />
            </div>
          </div>
          <button
            disabled={isLoading || !promotionCode}
            className={`button-generate ${isLoading || !promotionCode ? 'disabled' : ''}`}
            type="submit"
            onClick={onSubmit}
          >
            {t('submit')}
          </button>
        </div>
      ) : (
        <div className="center-content-col gap-3 withdraw--success">
          <SuccessIcon width="40" height="40" />
          <p className="title">{t('promotion.success.title')}</p>
          <p className="message">
            {usePromotionResponse.type === 'DOUBLE_LIMIT'
              ? t('promotion.success.doubleLimitMessage', {
                  amount: usePromotionResponse.maxAmount,
                })
              : t('promotion.success.message', {
                  amount: usePromotionResponse.amount,
                  type: usePromotionResponse.type.toLowerCase(),
                })}
          </p>
          <p className="message">{t('promotion.success.expireLimit')}</p>
          <button type="button" className="continue" onClick={onCloseModal}>
            {t('promotion.close')}
          </button>
        </div>
      )}
    </Modal>
  );
};

export default PromotionModal;
